import { lookupAirportCoords } from "./airportCoords";
import { haversineNm } from "./geo";
import { formatTripRunwayBrief, resolveAirportRunway } from "./airportRunways";
import {
  HAWKER_800XP,
  HAWKER_800XP_FERRY_RANGE_NM,
  HAWKER_800XP_SEED_HOURS,
  HAWKER_800XP_SEED_NEXT_INSPECTION,
  hawkerCabinLabel,
  hawkerEngineLabel,
  hawkerPlanningBurnGph,
  seedHawkerInspections,
} from "./hawker800xp";
import { safeTrim } from "./text";
import type { AircraftSpec, AirportRecord, AirportTag, SceneryAirport } from "./types";

export const HAWKER: AircraftSpec = {
  id: "hawker-800xp",
  name: HAWKER_800XP.variantLabel,
  registration: "C-GNSH",
  callsignPrefix: "CEL",
  icaoType: HAWKER_800XP.icaoType,
  cruiseKt: HAWKER_800XP.maxCruiseKt,
  longRangeCruiseKt: HAWKER_800XP.longRangeCruiseKt,
  rangeNm: HAWKER_800XP.rangeNm,
  ferryRangeNm: HAWKER_800XP_FERRY_RANGE_NM,
  minRunwayFt: HAWKER_800XP.takeoffDistanceFt,
  takeoffDistanceFt: HAWKER_800XP.takeoffDistanceFt,
  landingDistanceFt: HAWKER_800XP.landingDistanceFt,
  maxPax: HAWKER_800XP.typicalPax,
  crewSeats: HAWKER_800XP.typicalCrew,
  maxPayloadLb: HAWKER_800XP.maxPayloadLb,
  fuelBurnGph: hawkerPlanningBurnGph(),
  taxiFuelGal: 40,
  reserveMin: 45,
  available: true,
  engines: hawkerEngineLabel(),
  engineCount: 2,
  ceilingFt: HAWKER_800XP.ceilingFt,
  cabin: hawkerCabinLabel(),
  year: "2004",
  role: "Medium jet — golf / sun / VIP",
  remarks: "Summer pad CYPD; winter pad CYHZ. Celtic Aviation Services primary charter jet. FlightFX Hawker 800XP (no winglets) — not 850XP / XP2 / XPR.",
  oewLb: HAWKER_800XP.bowLb,
  mzfwLb: HAWKER_800XP.mzfwLb,
  mtowLb: HAWKER_800XP.mtowLb,
  mrwLb: HAWKER_800XP.mrwLb,
  mlwLb: HAWKER_800XP.mlwLb,
  maxFuelLb: HAWKER_800XP.maxFuelLb,
  airframeHours: HAWKER_800XP_SEED_HOURS,
  cycles: 3180,
  nextInspectionDueHours: HAWKER_800XP_SEED_NEXT_INSPECTION,
  inspections: seedHawkerInspections(HAWKER_800XP_SEED_HOURS),
  status: "available",
  baseIcao: "CYPD",
  lastAirportIcao: "CYPD",
};

export const PC12: AircraftSpec = {
  id: "pc12-ng",
  name: "Pilatus PC-12 NG",
  registration: "C-GNSP",
  callsignPrefix: "CEL",
  icaoType: "PC12",
  cruiseKt: 285,
  rangeNm: 1560,
  ferryRangeNm: 1845,
  minRunwayFt: 2580,
  maxPax: 8,
  fuelBurnGph: 72,
  taxiFuelGal: 12,
  reserveMin: 45,
  available: true,
  engines: "1 × P&W Canada PT6A-67P",
  engineCount: 1,
  ceilingFt: 30000,
  cabin: "Executive 6 / utility 8",
  year: "2018",
  role: "Utility / medevac / short strip",
  remarks: "Cape Breton shorts, medical, and strips the jet will not take.",
  oewLb: 6780,
  mzfwLb: 9040,
  mtowLb: 10450,
  mlwLb: 9921,
  maxFuelLb: 2704,
  airframeHours: 1860,
  cycles: 1640,
  nextInspectionDueHours: 2060,
  status: "available",
  baseIcao: "CYPD",
  lastAirportIcao: "CYPD",
};

export const FLEET: AircraftSpec[] = [HAWKER, PC12];

export function cloneSpec(spec: AircraftSpec): AircraftSpec {
  return {
    ...spec,
    inspections: spec.inspections?.map((item) => ({ ...item })),
  };
}

/** Seed copies so Fleet CRUD never mutates the catalog constants. */
export function seedFleet(): AircraftSpec[] {
  return [cloneSpec(HAWKER), cloneSpec(PC12)];
}

export function newAircraftId(registration = "C-GNEW"): string {
  const slug = registration.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "tail";
  return `ac-${slug}-${Date.now().toString(36).slice(-4)}`;
}

/** New tail. Every performance and weight figure starts empty — nothing is copied from another type. */
export const DEFAULT_INSPECTION_INTERVAL_HOURS = 200;

/** A new tail is due one interval past its typed hours, never at hour zero. */
export function blankAircraft(overrides: Partial<AircraftSpec> = {}): AircraftSpec {
  const merged: AircraftSpec = {
    id: "",
    name: "",
    registration: "",
    callsignPrefix: "",
    icaoType: "",
    cruiseKt: 0,
    rangeNm: 0,
    ferryRangeNm: 0,
    minRunwayFt: 0,
    maxPax: 0,
    fuelBurnGph: 0,
    taxiFuelGal: 0,
    reserveMin: 0,
    available: true,
    engines: "",
    ceilingFt: 0,
    cabin: "",
    year: "",
    role: "",
    remarks: "",
    oewLb: 0,
    mzfwLb: 0,
    mtowLb: 0,
    mlwLb: 0,
    maxFuelLb: 0,
    airframeHours: 0,
    engineHours: 0,
    apuHours: 0,
    cycles: 0,
    nextInspectionDueHours: 0,
    status: "available",
    baseIcao: "",
    lastAirportIcao: "",
    ...overrides,
  };
  if (overrides.nextInspectionDueHours === undefined) {
    merged.nextInspectionDueHours = Math.round((merged.airframeHours + DEFAULT_INSPECTION_INTERVAL_HOURS) * 10) / 10;
  }
  if (!overrides.id) merged.id = newAircraftId(merged.registration || "tail");
  return inheritAirframeTypeProfile(merged);
}

/** Normalize the ICAO designator. Performance stays whatever the tail already has. */
export function inheritAirframeTypeProfile(spec: AircraftSpec): AircraftSpec {
  const icaoType = spec.icaoType.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
  if (icaoType === spec.icaoType) return spec;
  return { ...spec, icaoType };
}

function apt(
  icao: string,
  name: string,
  city: string,
  country: string,
  lat: number,
  lon: number,
  runwayFt: number,
  tags: AirportTag[] = [],
  extra: Partial<AirportRecord> = {},
): AirportRecord {
  return { icao, name, city, country, lat, lon, runwayFt, tags, ...extra };
}

/** Catalog used by the generator when scenery has no lat/lon. */
export const AIRPORTS: AirportRecord[] = [
  apt("CYPD", "Port Hawkesbury", "Port Hawkesbury", "Canada", 45.6567, -61.3681, 5000, ["home", "golf"], {
    iata: "YPS",
    notes: "Celtic Aviation Services home / Cabot Links gateway",
  }),
  apt("CYHZ", "Halifax Stanfield Intl", "Halifax", "Canada", 44.8808, -63.5086, 10500, ["home", "corporate", "medical", "maintenance"], {
    iata: "YHZ",
    notes: "Winter pad / Atlantic mx",
    serviceCenter: true,
    serviceLabel: "Halifax jet / turboprop mx",
  }),
  apt("CYQY", "Sydney J.A. Douglas McCurdy", "Sydney", "Canada", 46.1614, -60.0478, 7070, ["medical", "corporate"], {
    iata: "YQY",
  }),
  apt("CYYT", "St. John's Intl", "St. John's", "Canada", 47.6186, -52.7519, 8502, ["corporate", "medical", "maintenance"], {
    iata: "YYT",
    serviceCenter: true,
    serviceLabel: "St. John's inspection pad",
  }),
  apt("CYQM", "Greater Moncton Roméo LeBlanc", "Moncton", "Canada", 46.1122, -64.6786, 10000, ["corporate", "maintenance"], {
    iata: "YQM",
    serviceCenter: true,
    serviceLabel: "Moncton Atlantic turboprop mx",
  }),
  apt("CYYG", "Charlottetown", "Charlottetown", "Canada", 46.29, -63.1211, 7000, ["corporate", "golf"], {
    iata: "YYG",
  }),
  apt("CYQX", "Gander Intl", "Gander", "Canada", 48.9369, -54.5681, 10100, ["maintenance"], {
    iata: "YQX",
    notes: "Atlantic tech stop",
    serviceCenter: true,
    serviceLabel: "Gander tech stop",
  }),
  apt("CYYZ", "Toronto Pearson Intl", "Toronto", "Canada", 43.6772, -79.6306, 11120, ["corporate", "golf", "vip", "maintenance"], {
    iata: "YYZ",
    serviceCenter: true,
    serviceLabel: "Toronto Pearson Part 145",
  }),
  apt("CYUL", "Montréal-Trudeau", "Montreal", "Canada", 45.4706, -73.7408, 11000, ["corporate", "golf", "vip", "maintenance"], {
    iata: "YUL",
    serviceCenter: true,
    serviceLabel: "Montréal business-jet centre",
  }),
  apt("CYOW", "Ottawa Macdonald-Cartier", "Ottawa", "Canada", 45.3225, -75.6692, 10000, ["corporate", "golf"], {
    iata: "YOW",
  }),
  apt("CYQB", "Québec City Jean Lesage", "Quebec City", "Canada", 46.7911, -71.3933, 9000, ["corporate"], {
    iata: "YQB",
  }),
  apt("KBEC", "Beech Factory", "Wichita", "United States", 37.6239, -97.2144, 8001, ["corporate"], {
    iata: "BEC",
    notes: "Hawker / Beech delivery field — career ferry origin example",
  }),
  apt("KBOS", "Boston Logan Intl", "Boston", "United States", 42.3656, -71.0096, 10005, ["golf", "corporate", "vip"], {
    iata: "BOS",
  }),
  apt("KTEB", "Teterboro", "Teterboro", "United States", 40.8501, -74.0608, 7000, ["vip", "corporate", "golf"], {
    iata: "TEB",
    notes: "NYC exec — default private-charter dest (not KJFK / KEWR)",
  }),
  apt("KHPN", "Westchester County", "White Plains", "United States", 41.067, -73.7076, 6549, ["vip", "corporate", "golf"], {
    iata: "HPN",
  }),
  apt("KBED", "Laurence G. Hanscom Fld", "Bedford", "United States", 42.47, -71.289, 7011, ["corporate", "vip", "golf"], {
    iata: "BED",
    notes: "Boston exec / reliever — prefer over KBOS for normal charter",
  }),
  apt("KMMU", "Morristown Muni", "Morristown", "United States", 40.7994, -74.4149, 5999, ["vip", "corporate"], {
    iata: "MMU",
  }),
  apt("KFRG", "Republic", "Farmingdale", "United States", 40.7288, -73.4134, 6827, ["vip", "corporate"], {
    iata: "FRG",
  }),
  apt("KORH", "Worcester Rgnl", "Worcester", "United States", 42.2673, -71.8757, 7000, ["corporate", "golf"], {
    iata: "ORH",
  }),
  apt("KSWF", "New York Stewart Intl", "Newburgh", "United States", 41.5041, -74.1048, 11818, ["corporate"], {
    iata: "SWF",
  }),
  apt("KHEF", "Manassas Rgnl", "Manassas", "United States", 38.7214, -77.5154, 5715, ["corporate", "vip"], {
    iata: "HEF",
  }),
  apt("KBDL", "Bradley Intl", "Windsor Locks", "United States", 41.9389, -72.6832, 9510, ["corporate"], {
    iata: "BDL",
  }),
  apt("KPVD", "Rhode Island T.F. Green", "Providence", "United States", 41.724, -71.4281, 7166, ["corporate", "golf"], {
    iata: "PVD",
  }),
  apt("KBTV", "Burlington Intl", "Burlington", "United States", 44.4719, -73.1533, 8320, ["corporate"], {
    iata: "BTV",
  }),
  apt("CYHU", "Montréal Saint-Hubert", "Longueuil", "Canada", 45.5175, -73.4169, 7842, ["corporate", "vip"], {
    iata: "YHU",
    notes: "Montreal exec / reliever — prefer over CYUL for normal charter",
  }),
  apt("CYKZ", "Buttonville Muni", "Toronto", "Canada", 43.8622, -79.37, 3902, ["corporate", "vip"], {
    iata: "YKZ",
    notes: "Toronto exec / reliever (MSFS still files; IRL closed 2023). Under 5000 ft — PIC warning only.",
  }),
  apt("CYTZ", "Billy Bishop Toronto City", "Toronto", "Canada", 43.6279, -79.3955, 3988, ["corporate", "vip"], {
    iata: "YTZ",
    notes: "Toronto Island exec. Under 5000 ft — PIC warning only.",
  }),
  apt("CYHM", "John C. Munro Hamilton", "Hamilton", "Canada", 43.1736, -79.935, 10000, ["corporate"], {
    iata: "YHM",
    notes: "Toronto reliever",
  }),
  apt("CYKF", "Region of Waterloo Intl", "Kitchener", "Canada", 43.4608, -80.3786, 7000, ["corporate"], {
    iata: "YKF",
  }),
  apt("CYOO", "Oshawa Exec", "Oshawa", "Canada", 43.9228, -78.895, 5000, ["corporate"], {
    iata: "YOO",
  }),
  apt("KJFK", "John F. Kennedy Intl", "New York", "United States", 40.6413, -73.7781, 14511, ["corporate", "golf"], {
    iata: "JFK",
  }),
  apt("KEWR", "Newark Liberty Intl", "Newark", "United States", 40.6895, -74.1745, 11000, ["corporate", "golf"], {
    iata: "EWR",
  }),
  apt("KDCA", "Ronald Reagan Washington Natl", "Washington", "United States", 38.8512, -77.0402, 7169, [
    "corporate",
    "vip",
    "golf",
    "medical",
  ], { iata: "DCA" }),
  apt("KIAD", "Washington Dulles Intl", "Dulles", "United States", 38.9531, -77.4565, 11500, ["corporate", "golf", "medical"], {
    iata: "IAD",
  }),
  apt("KPHL", "Philadelphia Intl", "Philadelphia", "United States", 39.8729, -75.2437, 10506, ["corporate", "golf", "medical"], {
    iata: "PHL",
  }),
  apt("KBWI", "Baltimore/Washington Intl", "Baltimore", "United States", 39.1754, -76.6683, 10502, ["corporate"], {
    iata: "BWI",
  }),
  apt("KMHT", "Manchester-Boston Rgnl", "Manchester", "United States", 42.9326, -71.4357, 9250, ["golf"], {
    iata: "MHT",
  }),
  apt("KACK", "Nantucket Memorial", "Nantucket", "United States", 41.2531, -70.0602, 6303, ["vip", "golf"], {
    iata: "ACK",
  }),
  apt("KBGR", "Bangor Intl", "Bangor", "United States", 44.8074, -68.8281, 11440, ["golf"], { iata: "BGR" }),
  apt("KPBI", "Palm Beach Intl", "West Palm Beach", "United States", 26.6832, -80.0956, 10001, ["sun", "florida", "golf"], {
    iata: "PBI",
  }),
  apt("KMIA", "Miami Intl", "Miami", "United States", 25.7959, -80.287, 13016, ["sun", "florida", "vip"], { iata: "MIA" }),
  apt("KCLT", "Charlotte Douglas Intl", "Charlotte", "United States", 35.214, -80.9431, 10000, ["carolinas", "golf", "sun"], {
    iata: "CLT",
  }),
  apt("KFLL", "Fort Lauderdale-Hollywood", "Fort Lauderdale", "United States", 26.0726, -80.1527, 9000, [
    "sun",
    "florida",
  ], { iata: "FLL" }),
  apt("KFXE", "Fort Lauderdale Exec", "Fort Lauderdale", "United States", 26.1973, -80.1707, 6001, [
    "sun",
    "florida",
    "vip",
  ], { iata: "FXE", notes: "FLL reliever — prefer over KFLL for normal charter" }),
  apt("KOPF", "Miami-Opa Locka Exec", "Miami", "United States", 25.907, -80.2784, 8002, ["sun", "florida", "vip"], {
    iata: "OPF",
    notes: "Miami exec — prefer over KMIA for normal charter",
  }),
  apt("KTPA", "Tampa Intl", "Tampa", "United States", 27.9755, -82.5332, 11002, ["sun", "florida"], { iata: "TPA" }),
  apt("KRSW", "Southwest Florida Intl", "Fort Myers", "United States", 26.5362, -81.7552, 12000, ["sun", "florida"], {
    iata: "RSW",
  }),
  apt("KAPF", "Naples Municipal", "Naples", "United States", 26.1526, -81.7755, 6600, ["sun", "florida", "vip", "maintenance"], {
    iata: "APF",
    serviceCenter: true,
    serviceLabel: "Pilatus / light jet mx",
  }),
  apt("KMCO", "Orlando Intl", "Orlando", "United States", 28.4312, -81.3081, 12005, ["sun", "florida"], { iata: "MCO" }),
  apt("KJAX", "Jacksonville Intl", "Jacksonville", "United States", 30.4941, -81.6879, 10000, ["sun", "florida"], {
    iata: "JAX",
  }),
  apt("KBCT", "Boca Raton", "Boca Raton", "United States", 26.3785, -80.1077, 6080, ["sun", "florida", "vip"], {
    iata: "BCT",
  }),
  apt("KCHS", "Charleston AFB/Intl", "Charleston", "United States", 32.8986, -80.0405, 9001, ["sun", "carolinas"], {
    iata: "CHS",
  }),
  apt("KMYR", "Myrtle Beach Intl", "Myrtle Beach", "United States", 33.6797, -78.9283, 9502, ["sun", "carolinas"], {
    iata: "MYR",
  }),
  apt("KILM", "Wilmington Intl", "Wilmington", "United States", 34.2706, -77.9026, 8016, ["sun", "carolinas"], {
    iata: "ILM",
  }),
  apt("TJSJ", "Luis Muñoz Marín Intl", "San Juan", "Puerto Rico", 18.4394, -66.0018, 10400, ["sun", "caribbean"], {
    iata: "SJU",
  }),
  apt("MYNN", "Lynden Pindling Intl", "Nassau", "Bahamas", 25.0389, -77.4662, 11353, ["sun", "caribbean", "vip"], {
    iata: "NAS",
  }),
  apt("TXKF", "L.F. Wade Intl", "Hamilton", "Bermuda", 32.364, -64.6787, 9705, ["sun", "vip"], { iata: "BDA" }),
  apt("TBPB", "Grantley Adams Intl", "Bridgetown", "Barbados", 13.0746, -59.4925, 11000, ["sun", "caribbean"], {
    iata: "BGI",
  }),
  apt("TNCM", "Princess Juliana Intl", "Philipsburg", "Sint Maarten", 18.041, -63.1089, 7708, ["sun", "caribbean"], {
    iata: "SXM",
  }),
  apt("MDPC", "Punta Cana Intl", "Punta Cana", "Dominican Republic", 18.5674, -68.3634, 10171, ["sun", "caribbean"], {
    iata: "PUJ",
  }),
  apt("MKJS", "Sangster Intl", "Montego Bay", "Jamaica", 18.5037, -77.9134, 8735, ["sun", "caribbean"], { iata: "MBJ" }),
  apt("MWCR", "Owen Roberts Intl", "George Town", "Cayman Islands", 19.2928, -81.3577, 7021, ["sun", "caribbean"], {
    iata: "GCM",
  }),
  apt("TNCA", "Queen Beatrix Intl", "Oranjestad", "Aruba", 12.5014, -70.0152, 9000, ["sun", "caribbean"], { iata: "AUA" }),
  apt("TIST", "Cyril E. King", "Charlotte Amalie", "U.S. Virgin Islands", 18.3373, -64.9734, 7000, ["sun", "caribbean"], {
    iata: "STT",
  }),
  apt("MMUN", "Cancún Intl", "Cancún", "Mexico", 21.0365, -86.8771, 11483, ["sun", "mexico"], {
    iata: "CUN",
    notes: "Hawker winter-sun / Mexico pack",
  }),
  apt("MMPR", "Lic. Gustavo Díaz Ordaz", "Puerto Vallarta", "Mexico", 20.6801, -105.2544, 10171, ["sun", "mexico"], {
    iata: "PVR",
  }),
  apt("MMSD", "Los Cabos Intl", "San José del Cabo", "Mexico", 23.1518, -109.7211, 9843, ["sun", "mexico", "vip"], {
    iata: "SJD",
  }),
  apt("MMTO", "Lic. Adolfo López Mateos", "Toluca", "Mexico", 19.3371, -99.566, 13780, ["corporate", "mexico", "vip"], {
    iata: "TLC",
    notes: "Mexico City bizjet alternate",
  }),
  apt("MMMX", "Mexico City Intl", "Mexico City", "Mexico", 19.4363, -99.0721, 12967, ["corporate", "mexico"], {
    iata: "MEX",
  }),
  apt("MMCZ", "Cozumel Intl", "Cozumel", "Mexico", 20.5224, -86.9256, 8858, ["sun", "mexico"], { iata: "CZM" }),
  apt("MMMY", "Monterrey Intl", "Monterrey", "Mexico", 25.7785, -100.107, 9843, ["corporate", "mexico"], {
    iata: "MTY",
  }),
  apt("KICT", "Wichita Dwight D. Eisenhower", "Wichita", "United States", 37.6499, -97.4331, 10301, ["maintenance"], {
    iata: "ICT",
    serviceCenter: true,
    serviceLabel: "Textron / Hawker service",
  }),
  apt("KCPT", "Cleburne Rgnl", "Cleburne", "United States", 32.3538, -97.4336, 5698, ["maintenance"], {
    serviceCenter: true,
    serviceLabel: "Airframe mx",
  }),
  apt("KDAY", "James M. Cox Dayton Intl", "Dayton", "United States", 39.9024, -84.2194, 10900, ["maintenance"], {
    iata: "DAY",
    serviceCenter: true,
    serviceLabel: "Duncan / Midwestern mx",
  }),
  apt("KGJT", "Grand Junction Rgnl", "Grand Junction", "United States", 39.1224, -108.5267, 10501, ["maintenance"], {
    iata: "GJT",
    serviceCenter: true,
    serviceLabel: "West mx",
  }),
  apt("KAPA", "Centennial", "Denver", "United States", 39.5701, -104.8493, 10000, ["maintenance"], {
    iata: "APA",
    serviceCenter: true,
    serviceLabel: "West mx / completions",
  }),
  apt("KSDL", "Scottsdale", "Scottsdale", "United States", 33.6229, -111.9105, 8249, ["maintenance", "sun", "vip"], {
    iata: "SDL",
    serviceCenter: true,
    serviceLabel: "Desert mx / completions",
  }),
  apt("KCHA", "Lovell Field", "Chattanooga", "United States", 35.0353, -85.2038, 7400, ["maintenance"], {
    iata: "CHA",
    serviceCenter: true,
    serviceLabel: "Airframe mx",
  }),
];

export const AIRPORT_BY_ICAO: Record<string, AirportRecord> = Object.fromEntries(
  AIRPORTS.map((a) => [a.icao, a]),
);

export const STARTER_ICAOS = [
  "CYPD",
  "CYHZ",
  "CYQY",
  "CYYT",
  "CYQM",
  "CYYG",
  "KTEB",
  "KHPN",
  "KBED",
  "CYHU",
  "CYHM",
  "CYKZ",
  "CYYZ",
  "CYUL",
  "CYOW",
  "KBOS",
  "KMIA",
  "KPBI",
  "KCLT",
  "MYNN",
  "KOPF",
  "KFXE",
] as const;

const STARTER_ICAO_SET = new Set<string>(STARTER_ICAOS);

/** Seed Destinations ICAO — company allow-list for filing, not Detect inventory. */
export function isStarterSceneryIcao(icao?: string | null): boolean {
  const code = safeTrim(icao).toUpperCase();
  return Boolean(code) && STARTER_ICAO_SET.has(code);
}

export function lookupAirport(icao?: string | null): AirportRecord | undefined {
  const code = safeTrim(icao).toUpperCase();
  return code ? AIRPORT_BY_ICAO[code] : undefined;
}

export type NearestCatalogAirport = {
  icao: string;
  name: string;
  nm: number;
};

/** Closest other airport in the desk catalog. Home itself is not a seed. */
export function nearestCatalogAirport(homeIcao?: string | null): NearestCatalogAirport | null {
  const home = lookupAirport(homeIcao);
  if (!home) return null;
  let best: NearestCatalogAirport | null = null;
  for (const row of AIRPORTS) {
    if (row.icao === home.icao) continue;
    const nm = Math.round(haversineNm(home.lat, home.lon, row.lat, row.lon));
    if (!best || nm < best.nm) best = { icao: row.icao, name: row.name, nm };
  }
  return best;
}

export function sceneryFromRecord(record: AirportRecord): SceneryAirport {
  return {
    id: record.icao,
    icao: record.icao,
    name: record.name,
    city: record.city,
    country: record.country,
    runwayFt: record.runwayFt,
    notes: record.notes ?? "",
    lat: record.lat,
    lon: record.lon,
  };
}

export function starterScenery(): SceneryAirport[] {
  return STARTER_ICAOS.map((icao) => {
    const rec = lookupAirport(icao);
    if (!rec) {
      return {
        id: icao,
        icao,
        name: icao,
        city: icao,
        country: "—",
        runwayFt: resolveAirportRunway(icao).longestFt,
        notes: "Starter placeholder",
      };
    }
    return sceneryFromRecord(rec);
  });
}

function applyResolvedRunway(code: string, existing?: { runwayFt?: number; notes?: string }): number {
  const rec = lookupAirport(code);
  return resolveAirportRunway(code, {
    runwayFt: existing?.runwayFt,
    notes: existing?.notes,
    catalogFt: rec?.runwayFt,
  }).longestFt;
}

/** Fill lat/lon/runway from the catalog + OurAirports when scenery is incomplete. */
export function hydrateSceneryAirport(icao?: string | null, scenery: SceneryAirport[] = []): SceneryAirport | undefined {
  const code = safeTrim(icao).toUpperCase();
  if (!code) return undefined;
  const hit = scenery.find((s) => safeTrim(s.icao).toUpperCase() === code);
  if (hit) {
    const rec = lookupAirport(hit.icao);
    const coords = lookupAirportCoords(hit.icao);
    return {
      ...hit,
      lat: hit.lat ?? rec?.lat ?? coords?.lat,
      lon: hit.lon ?? rec?.lon ?? coords?.lon,
      runwayFt: applyResolvedRunway(code, hit),
      city: hit.city || rec?.city || hit.name,
      country: hit.country || rec?.country || "—",
    };
  }
  const rec = lookupAirport(code);
  if (rec) return sceneryFromRecord(rec);
  const coords = lookupAirportCoords(code);
  const runwayFt = applyResolvedRunway(code);
  if (!coords && runwayFt <= 0) return undefined;
  return {
    id: code,
    icao: code,
    name: code,
    city: code,
    country: "—",
    runwayFt,
    notes: "",
    lat: coords?.lat,
    lon: coords?.lon,
  };
}

/** Same origin/dest runway line + PIC warning used by Inbox, board, and Megan. */
export function tripRunwayBriefForIcaos(
  originIcao?: string | null,
  destIcao?: string | null,
  scenery: SceneryAirport[] = [],
): { line: string; warning: string | null } {
  const origin = hydrateSceneryAirport(originIcao, scenery);
  const dest = hydrateSceneryAirport(destIcao, scenery);
  const originCode = safeTrim(originIcao).toUpperCase();
  const destCode = safeTrim(destIcao).toUpperCase();
  if (!originCode || !destCode) return { line: "", warning: null };
  return formatTripRunwayBrief(
    { icao: originCode, runwayFt: origin?.runwayFt },
    { icao: destCode, runwayFt: dest?.runwayFt },
  );
}

export function resolveCoords(
  icao?: string | null,
  scenery: SceneryAirport[] = [],
): { lat: number; lon: number } | null {
  const code = safeTrim(icao).toUpperCase();
  if (!code) return null;
  const fromScenery = scenery.find((s) => safeTrim(s.icao) === code);
  if (fromScenery?.lat != null && fromScenery.lon != null) {
    return { lat: fromScenery.lat, lon: fromScenery.lon };
  }
  const rec = lookupAirport(code);
  if (rec) return { lat: rec.lat, lon: rec.lon };
  return lookupAirportCoords(code);
}
